// Updated: 2026-08-20 10:15:00 CDT
// Purpose: Parse FDX, Fountain, and plain-text screenplays into one ordered audio-ready model.

import type { Character, SequenceItem } from "@/types/screenplay";

export type ScreenplayFormat = "fdx" | "fountain" | "txt";

export interface ScreenplayParseResult {
  characters: Character[];
  sequence: SequenceItem[];
  warnings: string[];
  stats: {
    dialogueItems: number;
    narrationItems: number;
    sceneHeadings: number;
    characterCount: number;
  };
}

const SCENE_HEADING_PATTERN = /^(?:\.|INT\.?\/EXT\.?|INT\.?|EXT\.?|EST\.?|I\.?\/E\.?)(?:\s|$)/i;
const TRANSITION_PATTERN = /^(?:FADE (?:IN|OUT)|CUT TO|DISSOLVE TO|SMASH CUT TO|MATCH CUT TO|WIPE TO)\s*:?$/i;
const CHARACTER_ANNOTATION_PATTERN = /\s*\((?:V\.?O\.?|O\.?S\.?|O\.?C\.?|CONT(?:'D|INUED)?|FILTERED|ON RADIO|ON PHONE)[^)]*\)\s*/gi;

const decodeXml = (value: string): string =>
  value
    .replace(/<!\[CDATA\[([\s\S]*?)\]\]>/g, "$1")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&apos;/gi, "'")
    .replace(/&#(\d+);/g, (_, code: string) => String.fromCodePoint(Number(code)))
    .replace(/&#x([0-9a-f]+);/gi, (_, code: string) => String.fromCodePoint(Number.parseInt(code, 16)))
    .replace(/\s+/g, " ")
    .trim();

const cleanCharacterName = (value: string): string =>
  value
    .replace(CHARACTER_ANNOTATION_PATTERN, " ")
    .replace(/\^\s*$/, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();

const descriptionFor = (lineCount: number): string => {
  if (lineCount > 20) return "Major character with significant dialogue";
  if (lineCount > 10) return "Supporting character";
  if (lineCount > 5) return "Minor character";
  return "Background character";
};

const assembleResult = (
  sequence: SequenceItem[],
  warnings: string[],
): ScreenplayParseResult => {
  const dialogueByCharacter = new Map<string, string[]>();
  const narration: string[] = [];

  for (const item of sequence) {
    if (item.type === "dialogue" && item.character) {
      const lines = dialogueByCharacter.get(item.character) ?? [];
      lines.push(item.text);
      dialogueByCharacter.set(item.character, lines);
    } else if (item.type !== "parenthetical") {
      narration.push(item.text);
    }
  }

  const speakers: Character[] = [...dialogueByCharacter.entries()]
    .map(([name, dialogue]) => ({
      name,
      lines: dialogue.length,
      description: descriptionFor(dialogue.length),
      dialogue,
    }))
    .sort((left, right) => right.lines - left.lines || left.name.localeCompare(right.name));

  const characters: Character[] = [];

  if (narration.length > 0) {
    characters.push({
      name: "SCENE NARRATOR",
      lines: narration.length,
      description: "Narrator voice for scene headings, descriptions, and action",
      dialogue: narration,
    });
  }

  characters.push(...speakers);

  if (sequence.length > 0) {
    characters.push({
      name: "SCREENPLAY_SEQUENCE",
      lines: sequence.length,
      description: "Complete ordered screenplay sequence",
      dialogue: [],
      sequence,
    });
  }

  if (sequence.length === 0) {
    warnings.push("No ordered screenplay content was recognized.");
  }

  if (dialogueByCharacter.size === 0) {
    warnings.push("No character dialogue was recognized.");
  }

  return {
    characters,
    sequence,
    warnings: [...new Set(warnings)],
    stats: {
      dialogueItems: sequence.filter((item) => item.type === "dialogue").length,
      narrationItems: sequence.filter(
        (item) => item.type !== "dialogue" && item.type !== "parenthetical",
      ).length,
      sceneHeadings: sequence.filter((item) => item.type === "scene-heading").length,
      characterCount: dialogueByCharacter.size,
    },
  };
};

const parseFdx = (source: string): ScreenplayParseResult => {
  const warnings: string[] = [];
  const sequence: SequenceItem[] = [];
  const contentMatch = source.match(/<Content(?:\s[^>]*)?>([\s\S]*?)<\/Content>/i);
  const content = contentMatch?.[1] ?? source;
  const paragraphs: string[] = content.match(/<Paragraph(?:\s[^>]*)?>[\s\S]*?<\/Paragraph>/gi) ?? [];
  let currentCharacter = "";

  if (paragraphs.length === 0) {
    warnings.push("No Final Draft paragraphs were found.");
  }

  paragraphs.forEach((paragraph, paragraphIndex) => {
    const openingTag = paragraph.match(/^<Paragraph([^>]*)>/i)?.[1] ?? "";
    const type = openingTag.match(/\bType\s*=\s*["']?([^"'\s>]+(?:\s+[^"'>]+)?)/i)?.[1]?.trim().toLowerCase() ?? "";
    const style = decodeXml(paragraph.match(/<ParaStyle[^>]*>([\s\S]*?)<\/ParaStyle>/i)?.[1] ?? "").toLowerCase();
    const textParts = [...paragraph.matchAll(/<Text[^>]*>([\s\S]*?)<\/Text>/gi)]
      .map((match) => decodeXml(match[1]))
      .filter(Boolean);
    const text = textParts.join("").replace(/\s+/g, " ").trim();

    if (!text) return;

    const isCharacter = type.includes("character") || style.includes("character");
    const isDialogue = type.includes("dialogue") || style.includes("dialogue");
    const isParenthetical = type.includes("parenthetical") || style.includes("parenthetical");
    const isSceneHeading = type.includes("scene heading") || style.includes("scene heading") || SCENE_HEADING_PATTERN.test(text);
    const isTransition = type.includes("transition") || style.includes("transition") || TRANSITION_PATTERN.test(text);

    if (isCharacter) {
      currentCharacter = cleanCharacterName(text);
      return;
    }

    if (isParenthetical) {
      sequence.push({
        type: "parenthetical",
        character: currentCharacter || undefined,
        text,
        order: paragraphIndex,
      });
      return;
    }

    if (isDialogue && currentCharacter) {
      sequence.push({
        type: "dialogue",
        character: currentCharacter,
        text,
        order: paragraphIndex,
      });
      return;
    }

    if (isTransition) {
      currentCharacter = "";
      return;
    }

    if (isSceneHeading) {
      sequence.push({ type: "scene-heading", text, order: paragraphIndex });
      currentCharacter = "";
      return;
    }

    sequence.push({ type: "action", text, order: paragraphIndex });
    currentCharacter = "";
  });

  return assembleResult(sequence, warnings);
};

const isCharacterCue = (
  line: string,
  format: Exclude<ScreenplayFormat, "fdx">,
  nextLine: string,
): boolean => {
  const forced = line.startsWith("@");
  const candidate = forced ? line.slice(1).trim() : line;

  if (!candidate || candidate.length > 60 || SCENE_HEADING_PATTERN.test(candidate) || TRANSITION_PATTERN.test(candidate)) {
    return false;
  }

  const uppercaseCue = candidate === candidate.toUpperCase() && /[A-Z]/.test(candidate);
  const titleCaseCue =
    format === "txt" &&
    /^[A-Z][\p{L}'’-]*(?:\s+[A-Z][\p{L}'’-]*){0,4}(?:\s*\([^)]*\))?$/u.test(candidate);
  const hasFollowingDialogue = Boolean(nextLine) && !SCENE_HEADING_PATTERN.test(nextLine) && !TRANSITION_PATTERN.test(nextLine);

  return (forced || uppercaseCue || titleCaseCue) && hasFollowingDialogue;
};

const parseText = (
  source: string,
  format: Exclude<ScreenplayFormat, "fdx">,
): ScreenplayParseResult => {
  const warnings: string[] = [];
  const sequence: SequenceItem[] = [];
  const lines = source.replace(/^\uFEFF/, "").replace(/\r\n?/g, "\n").split("\n");
  let index = 0;

  while (index < lines.length) {
    const text = lines[index].trim();

    if (!text || /^\/\*/.test(text) || /^\[\[.*\]\]$/.test(text)) {
      index += 1;
      continue;
    }

    if (index < 20 && /^(?:Title|Credit|Author|Source|Draft date|Contact):/i.test(text)) {
      index += 1;
      continue;
    }

    if (SCENE_HEADING_PATTERN.test(text)) {
      sequence.push({
        type: "scene-heading",
        text: text.startsWith(".") ? text.slice(1).trim() : text,
        order: index,
      });
      index += 1;
      continue;
    }

    if (TRANSITION_PATTERN.test(text) || /^>.*<$/.test(text)) {
      index += 1;
      continue;
    }

    const nextLine = lines[index + 1]?.trim() ?? "";

    if (isCharacterCue(text, format, nextLine)) {
      const character = cleanCharacterName(text.replace(/^@/, ""));
      index += 1;

      while (index < lines.length) {
        const dialogueLine = lines[index].trim();

        if (!dialogueLine) break;
        if (/^\([^)]*\)$/.test(dialogueLine)) {
          sequence.push({
            type: "parenthetical",
            character,
            text: dialogueLine,
            order: index,
          });
          index += 1;
          continue;
        }
        if (SCENE_HEADING_PATTERN.test(dialogueLine) || TRANSITION_PATTERN.test(dialogueLine)) break;

        sequence.push({
          type: "dialogue",
          character,
          text: dialogueLine,
          order: index,
        });
        index += 1;
      }

      continue;
    }

    sequence.push({ type: "action", text, order: index });
    index += 1;
  }

  return assembleResult(sequence, warnings);
};

export const parseScreenplay = (
  source: string,
  format: ScreenplayFormat,
): ScreenplayParseResult => {
  const normalizedSource = source.trim();

  if (!normalizedSource) {
    return assembleResult([], ["The screenplay file is empty."]);
  }

  const detectedFdx = /<FinalDraft\b/i.test(normalizedSource) || /<Content\b/i.test(normalizedSource);

  if (format === "fdx" || detectedFdx) {
    return parseFdx(normalizedSource);
  }

  return parseText(normalizedSource, format);
};
